import { pricingLadderCopy } from './landingPricingLadderCopy';
import { formatSkuPrice, type PublicSku } from './marketing/landingPricingSource';
import type { LandingProductCopy } from './landingProductCopy';
import type { LandingStorySnapshot } from './landingStorySnapshot';
import type {
  FmProductActionDescriptor,
  FmProductSectionDescriptor,
} from '@/components/ui/FmProductPage';
import type { Language } from '@/i18n';

interface LandingCatalogCallbacks {
  addForest: () => void;
  selectSku: (skuId: string) => void;
}

/** The served price basis, or the reason the catalog cannot price this record. */
interface CatalogPricing {
  priceLabel: string;
  priceStatus: 'available' | 'unavailable';
  annual: boolean;
}

function catalogPrimaryAction(input: {
  skuId: string;
  pricing: CatalogPricing;
  checkoutAvailable: boolean;
  checkoutUnavailable: string;
  localized: LandingProductCopy['offerDecode'];
  callbacks: LandingCatalogCallbacks;
}): FmProductActionDescriptor {
  const { skuId, pricing, checkoutAvailable, checkoutUnavailable, localized, callbacks } = input;
  if (pricing.priceStatus === 'available' && checkoutAvailable) {
    return {
      id: `landing-select-${skuId}`,
      label: localized.actionLabel,
      intent: 'primary',
      onSelect: () => callbacks.selectSku(skuId),
    };
  }
  return {
    id: `landing-select-${skuId}`,
    label: localized.actionLabel,
    intent: 'primary',
    disabledReason:
      pricing.priceStatus === 'available' ? checkoutUnavailable : pricing.priceLabel,
  };
}

function catalogSection(input: {
  skuId: string;
  locale: Language;
  pricing: CatalogPricing;
  checkoutAvailable: boolean;
  localized: LandingProductCopy['offerDecode'];
  copy: LandingProductCopy;
  callbacks: LandingCatalogCallbacks;
}): FmProductSectionDescriptor {
  const { skuId, locale, pricing, checkoutAvailable, localized, copy, callbacks } = input;
  const ladder = pricingLadderCopy(locale);
  const tier =
    skuId === 'plot_package'
      ? ladder.owner
      : skuId === 'portfolio_estate'
        ? ladder.portfolio
        : null;
  return {
    id: `landing-catalog-${skuId}`,
    capability: 'catalog-decision-cta',
    role: tier ? 'lead' : 'detail',
    title: localized.name,
    state: { status: 'ready' },
    answer: tier?.benefit ?? localized.teaser,
    catalog: {
      recordId: skuId,
      offerLabel: localized.name,
      priceLabel: pricing.priceLabel,
      priceStatus: pricing.priceStatus,
      prominence: skuId === 'plot_package' ? 'primary' : 'secondary',
      audience: tier ? 'owner' : 'transaction',
      groupLabel: tier ? ladder.ownerHeading : ladder.transactionsHeading,
      ...(tier ? { inheritanceLabel: tier.inheritance } : {}),
      billingLabel: pricing.annual ? copy.labels.annual : copy.labels.oneOff,
      entitlementLabel: localized.summary,
      includes: tier?.includes ?? localized.includes,
      ...(skuId === 'offer_decode'
        ? {
            relationship: {
              label: copy.labels.funnelIncludesYear,
              targetRecordId: 'plot_package',
              targetLabel: copy.plotPackage.name,
            },
          }
        : {}),
    },
    primaryAction: catalogPrimaryAction({
      skuId,
      pricing,
      checkoutAvailable,
      checkoutUnavailable: ladder.checkoutUnavailable,
      localized,
      callbacks,
    }),
  };
}

function servedPricing(sku: PublicSku, locale: Language): CatalogPricing {
  return {
    priceLabel: formatSkuPrice(sku, locale),
    priceStatus: 'available',
    annual: sku.cadence === 'annual',
  };
}

function unpricedPreview(skuId: string, copy: LandingProductCopy): CatalogPricing {
  return {
    priceLabel: copy.state.stale,
    priceStatus: 'unavailable',
    annual: skuId === 'plot_package' || skuId === 'portfolio_estate',
  };
}

export function buildLandingStartSections(input: {
  story: LandingStorySnapshot | null;
  loading: boolean;
  locale: Language;
  copy: LandingProductCopy;
  callbacks: LandingCatalogCallbacks;
}): [FmProductSectionDescriptor, ...FmProductSectionDescriptor[]] {
  const { story, loading, locale, copy, callbacks } = input;
  const ladder = pricingLadderCopy(locale);
  const free: FmProductSectionDescriptor = {
    id: 'landing-start-free',
    capability: 'catalog-decision-cta',
    role: 'lead',
    title: ladder.free.name,
    state: { status: 'ready' },
    answer: ladder.free.benefit,
    catalog: {
      recordId: 'free',
      offerLabel: ladder.free.scope,
      priceLabel: ladder.free.name,
      priceStatus: 'available',
      prominence: 'secondary',
      audience: 'owner',
      groupLabel: ladder.ownerHeading,
      billingLabel: ladder.free.scope,
      entitlementLabel: ladder.free.benefit,
      scopeLabel: ladder.free.boundary,
      includes: ladder.free.includes,
    },
    primaryAction: {
      id: 'landing-start-add-free',
      label: ladder.free.action,
      intent: 'primary',
      onSelect: callbacks.addForest,
    },
  };
  const localizedCatalogCopy: Record<string, LandingProductCopy['offerDecode']> = {
    offer_decode: copy.offerDecode,
    plot_package: copy.plotPackage,
    buyer_decode: copy.buyerDecode,
    portfolio_estate: copy.estate,
  };
  const catalogOrder = ['plot_package', 'portfolio_estate', 'offer_decode', 'buyer_decode'];
  const choices = catalogOrder.map((skuId) => {
    const sku = story?.catalog.available
      ? story.catalog.skus.find((entry) => entry.id === skuId)
      : undefined;
    const pricing = sku ? servedPricing(sku, locale) : unpricedPreview(skuId, copy);
    if (!sku && loading) pricing.priceLabel = copy.state.loading;
    return catalogSection({
      skuId,
      locale,
      pricing,
      checkoutAvailable: story?.catalog.checkoutAvailable === true,
      localized: localizedCatalogCopy[skuId],
      copy,
      callbacks,
    });
  });
  return [free, ...choices];
}
